/**
 * OmsProposalsPage — dedicated view for builder_quote proposals (F-2.0 Phase 4+5).
 *
 * Filters:
 *  - source=builder_quote (fixed, always)
 *  - proposal_status (dropdown)
 *  - search, date range (inherited from base orders pattern)
 *
 * Role guard: isCommercial() || isAdmin() (same as OmsOrdersPage)
 * Clicking a row opens ProposalDetail modal (not OmsOrderDetail).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Pagination,
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Typography,
  Chip,
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { useLayoutContext } from "../context/LayoutContext";
import api from "../hooks/api";
import { API_URLS } from "../App";
import TenantSelector from "../components/common/TenantSelector";
import { useTenantSelection } from "../hooks/useTenantSelection";
import { OmsOrder, OmsOrdersListResponse } from "../model/Oms";
import {
  ProposalStatus,
  PROPOSAL_STATUS_LABELS,
  PROPOSAL_STATUS_COLOR,
} from "../model/Proposal";
import ProposalStatusChip from "../components/Proposals/ProposalStatusChip";
import ProposalDetail from "../components/Proposals/ProposalDetail";
import { orderShortRef } from "../helpers/orderRef";

const ALL_PROPOSAL_STATUSES: ProposalStatus[] = [
  "DRAFT",
  "DRAFT_SENT",
  "ACCEPTED",
  "DECLINED",
  "CHANGE_REQUESTED",
  "EXPIRED",
  "CANCELLED",
];

function getInitialParam(key: string, fallback = ""): string {
  return new URLSearchParams(window.location.search).get(key) ?? fallback;
}

const OmsProposalsPage: React.FC = () => {
  const { profile, isAuthenticated } = useAuth();
  const { setModalScreen, setError } = useLayoutContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: proposalId } = useParams<{ id?: string }>();
  const authed = isAuthenticated();

  const [userProfile, setUserProfile] = useState<ProfileAndSettings | null>(
    null,
  );
  const [profileLoading, setProfileLoading] = useState(true);

  const [orders, setOrders] = useState<OmsOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [search, setSearch] = useState(() => getInitialParam("search"));
  const [proposalStatus, setProposalStatus] = useState<ProposalStatus | "">(
    () => {
      const raw = getInitialParam("proposal_status");
      // Guard against hand-edited ?proposal_status=FOO (MUI Select with no
      // matching MenuItem would warn and render an empty dropdown).
      return (ALL_PROPOSAL_STATUSES as string[]).includes(raw)
        ? (raw as ProposalStatus)
        : "";
    },
  );
  const [dateFrom, setDateFrom] = useState(() => getInitialParam("from"));
  const [dateTo, setDateTo] = useState(() => getInitialParam("to"));

  // ── Multi-tenant selection (ADR-021) ─────────────────────────────────────
  // The builder_quote orders live under the operator's resolved tenant; the
  // selector lets them switch to it when a different tenant is persisted from
  // another page (the interceptor forwards X-Selected-Tenant-ID globally).
  const { tenants, selectedTenant, selectTenant } = useTenantSelection();

  useEffect(() => {
    profile()
      .then((p) => {
        setUserProfile(p);
        setProfileLoading(false);
      })
      .catch(() => setProfileLoading(false));
  }, [profile]);

  // ── Auth guard: preserve deep link across login ─────────────────────────

  useEffect(() => {
    if (!authed) {
      // Same pattern as other pages (e.g. CategoriesManagementPage): LoginPage
      // reads location.state.from and navigates back after successful login.
      navigate("/login", {
        state: { from: location.pathname + location.search },
        replace: true,
      });
    }
  }, [authed, navigate, location.pathname, location.search]);

  const fetchOrders = useCallback(
    async (
      p: number,
      filters?: {
        search: string;
        proposalStatus: string;
        dateFrom: string;
        dateTo: string;
      },
    ) => {
      setLoading(true);
      setFetchError(null);
      try {
        const s = filters?.search ?? search;
        const ps = filters?.proposalStatus ?? proposalStatus;
        const from = filters?.dateFrom ?? dateFrom;
        const to = filters?.dateTo ?? dateTo;
        const params = new URLSearchParams({
          page: String(p),
          per_page: "25",
          source: "builder_quote", // fixed filter
          sort_by: "created_at",
          sort_dir: "desc",
        });
        if (s) params.set("search", s);
        if (ps) params.set("proposal_status", ps);
        if (from) params.set("from", from);
        if (to) params.set("to", to);

        // Sync the browser URL with the effective filters so searches are
        // bookmarkable/shareable (mirrors OmsOrdersPage behaviour).
        history.replaceState(
          history.state,
          "",
          `/oms-proposals?${params.toString()}`,
        );

        const res = await api.get<OmsOrdersListResponse>(
          `${API_URLS.OMS_ADMIN_ORDERS}?${params}`,
        );
        setOrders(res.data.orders || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.total_pages || 1);
        setPage(p);
      } catch (e: any) {
        if ([401, 403].includes(e?.response?.status)) {
          setError("Sem permissão para aceder às propostas.");
        } else {
          setFetchError(
            e?.response?.data?.error || "Erro ao carregar propostas.",
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [search, proposalStatus, dateFrom, dateTo, setError],
  );

  useEffect(() => {
    if (!profileLoading && userProfile) {
      fetchOrders(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileLoading, userProfile]);

  const openProposalDetail = (order: OmsOrder) => {
    // userProfile is guaranteed non-null here — row click is only reachable
    // after the role gate below. The early return is purely a TypeScript
    // narrowing aid.
    if (!userProfile) return;
    const detailPath = `/oms-proposals/${order.id}`;
    // When arriving via deep link the URL already is the detail path — don't
    // push a duplicate history entry (would make Back reopen the modal after
    // close). Only push when navigating from the list.
    if (window.location.pathname !== detailPath) {
      history.pushState(history.state, "", detailPath);
    }
    setModalScreen({
      title: `Proposta — ${orderShortRef(order.id)}`,
      state: "big-view",
      childrenControlOverflow: true,
      children: (
        <ProposalDetail
          order={order}
          userProfile={userProfile}
          onOrderUpdated={(updated) => {
            setOrders((prev) =>
              prev.map((o) => (o.id === updated.id ? updated : o)),
            );
          }}
        />
      ),
      onClose: () => {
        // Restore the list URL with the active filters intact (fetchOrders
        // syncs them via replaceState; don't wipe them on modal close).
        // replaceState (not pushState): the detail entry is replaced so browser
        // Back after closing doesn't reopen the modal.
        const params = new URLSearchParams({
          page: String(page),
          per_page: "25",
        });
        if (search) params.set("search", search);
        if (proposalStatus) params.set("proposal_status", proposalStatus);
        if (dateFrom) params.set("from", dateFrom);
        if (dateTo) params.set("to", dateTo);
        history.replaceState(
          history.state,
          "",
          `/oms-proposals?${params.toString()}`,
        );
        setModalScreen(null);
      },
    });
  };

  // ── Deep link: /oms-proposals/:id opens the detail directly ─────────────

  const deepLinkOpenedRef = useRef(false);

  useEffect(() => {
    if (!proposalId || profileLoading || !userProfile) return;
    if (!userProfile.isAdmin() && !userProfile.isCommercial()) return;
    if (deepLinkOpenedRef.current) return;
    deepLinkOpenedRef.current = true;

    let cancelled = false;
    api
      .get<OmsOrder>(`${API_URLS.OMS_ADMIN_ORDERS}/${proposalId}`)
      .then((res) => {
        if (!cancelled) openProposalDetail(res.data);
      })
      .catch((e: any) => {
        if (cancelled) return;
        // Unknown/invalid proposal — fall back to the list with an error banner.
        history.replaceState(history.state, "", "/oms-proposals");
        setError(
          e?.response?.status === 404
            ? "Proposta não encontrada."
            : e?.response?.data?.error || "Erro ao abrir a proposta.",
        );
      });
    return () => {
      cancelled = true;
    };
    // openProposalDetail is recreated per render; the ref gate guarantees a
    // single run per proposalId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalId, profileLoading, userProfile]);

  if (!authed) return null;
  if (profileLoading)
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  if (!userProfile?.isAdmin() && !userProfile?.isCommercial())
    return <Navigate to="/" replace />;

  return (
    <Box p={4}>
      <Paper elevation={1} sx={{ p: 3 }}>
        {/* Header */}
        <Typography variant="h5" fontWeight={700} mb={0.5}>
          Propostas de Orçamento
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Encomendas do canal builder_quote. Cria, envia e gere propostas.
        </Typography>

        {/* Filters */}
        <Box
          display="flex"
          gap={2}
          flexWrap="wrap"
          mb={3}
          alignItems="flex-end"
        >
          <TenantSelector
            tenants={tenants}
            value={selectedTenant}
            onChange={(t) => {
              selectTenant(t);
              fetchOrders(1);
            }}
            className="min-w-[180px]"
          />
          <TextField
            label="Pesquisar"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchOrders(1)}
            sx={{ minWidth: 220 }}
            placeholder="nome, email, ref…"
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Estado proposta</InputLabel>
            <Select
              label="Estado proposta"
              value={proposalStatus}
              onChange={(e) =>
                setProposalStatus(e.target.value as ProposalStatus | "")
              }
            >
              <MenuItem value="">Todos</MenuItem>
              {ALL_PROPOSAL_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {PROPOSAL_STATUS_LABELS[s]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="De"
            type="date"
            size="small"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Até"
            type="date"
            size="small"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Box display="flex" gap={1}>
            <button
              className="button is-primary is-small"
              onClick={() => fetchOrders(1)}
            >
              Pesquisar
            </button>
            {(search || proposalStatus || dateFrom || dateTo) && (
              <button
                className="button is-light is-small"
                onClick={() => {
                  setSearch("");
                  setProposalStatus("");
                  setDateFrom("");
                  setDateTo("");
                  // Fetch with explicit empty filters — using the state
                  // closure here would refetch with the pre-clear values.
                  fetchOrders(1, {
                    search: "",
                    proposalStatus: "",
                    dateFrom: "",
                    dateTo: "",
                  });
                }}
              >
                Limpar
              </button>
            )}
          </Box>
        </Box>

        {/* Results count */}
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mb={1}
        >
          {loading ? "A carregar…" : `${total} resultado(s)`}
        </Typography>

        {fetchError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {fetchError}
          </Alert>
        )}

        {/* Table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{ "& th": { fontWeight: 700, bgcolor: "#f5f5f5" } }}
              >
                <TableCell>Ref.</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Estado proposta</TableCell>
                <TableCell>Versão</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Criada em</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && orders.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ py: 4, color: "text.secondary" }}
                  >
                    Nenhuma proposta encontrada.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                orders.map((order) => (
                  <TableRow
                    key={order.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => openProposalDetail(order)}
                  >
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 13 }}>
                      {orderShortRef(order.id)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {order.customer_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {order.customer_email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {order.proposal_status ? (
                        <ProposalStatusChip status={order.proposal_status} />
                      ) : (
                        <Chip label="Sem proposta" size="small" />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {order.proposal_version != null
                        ? `v${order.proposal_version}`
                        : "—"}
                    </TableCell>
                    <TableCell align="right">
                      {((order.total_cents ?? 0) / 100).toFixed(2)} €
                    </TableCell>
                    <TableCell>
                      {new Date(order.created_at).toLocaleDateString("pt-PT")}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, p) => fetchOrders(p)}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default OmsProposalsPage;
